import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics'
import type { CustomKeyAndValue, StackFrame } from '@capacitor-firebase/crashlytics'
import { loggerService } from '../logger/loggerService'
import { formatRecentLogs } from '../logger/logBuffer'

/**
 * Crashlytics only exists on the native layer, so every call is a no-op on web.
 *
 * We also check that the plugin is actually registered rather than just testing for
 * a native platform: this app ships JS via OTA, so a bundle containing these calls
 * can land on an older APK that has no Crashlytics plugin. Without the check, every
 * report would become a rejected bridge call.
 */
const canReport = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FirebaseCrashlytics')

// Crashlytics caps a custom key value at 1024 chars and drops longer stacks anyway.
const MAX_KEY_VALUE_LENGTH = 1024
const MAX_STACK_FRAMES = 64
// Crashlytics caps the log section of a report at 64 KB; stay well under it and keep
// the most recent lines, which are the ones closest to the failure.
const MAX_BREADCRUMB_LENGTH = 32 * 1024

// V8 stack frame, e.g. "    at doThing (https://host/static/js/main.js:12:34)"
// or the anonymous form "    at https://host/static/js/main.js:12:34".
const STACK_FRAME_PATTERN = /^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/

// Tracked separately: the handlers must only ever be attached once, but a failed
// native init should stay retryable on a later call.
let handlersInstalled = false
let nativeInitialized = false

const truncate = (value: string): string =>
  value.length > MAX_KEY_VALUE_LENGTH ? `${value.slice(0, MAX_KEY_VALUE_LENGTH - 1)}…` : value

const normalizeError = (error: unknown): { message: string; stack?: string } => {
  if (error instanceof Error) {
    return { message: `${error.name}: ${error.message}`, stack: error.stack }
  }

  if (typeof error === 'string') {
    return { message: error }
  }

  try {
    return { message: JSON.stringify(error) ?? String(error) }
  } catch {
    return { message: String(error) }
  }
}

const parseStack = (stack?: string): StackFrame[] | undefined => {
  if (!stack) {
    return undefined
  }

  const frames = stack
    .split('\n')
    .map(line => STACK_FRAME_PATTERN.exec(line))
    .filter((match): match is RegExpExecArray => match !== null)
    .slice(0, MAX_STACK_FRAMES)
    .map(match => ({
      functionName: match[1] || '<anonymous>',
      fileName: match[2],
      lineNumber: Number(match[3]),
    }))

  return frames.length ? frames : undefined
}

const toKeysAndValues = (context?: Record<string, string>): CustomKeyAndValue[] | undefined => {
  const entries = Object.entries(context ?? {})

  if (!entries.length) {
    return undefined
  }

  return entries.map(([key, value]) => ({ key, value: truncate(value), type: 'string' as const }))
}

/**
 * Reports a non-fatal error to Crashlytics. Never throws — reporting a problem
 * must not be able to create a second one.
 */
export const recordError = async (
  error: unknown,
  context?: Record<string, string>
): Promise<void> => {
  // Snapshot before the console.error below, so the report's breadcrumbs end at the
  // last thing the app did rather than at our own logging.
  const breadcrumbs = canReport() ? formatRecentLogs(MAX_BREADCRUMB_LENGTH) : ''

  const { message, stack } = normalizeError(error)
  console.error('[Crashlytics] non-fatal:', message, context ?? '')

  if (!canReport()) {
    return
  }

  try {
    if (breadcrumbs) {
      await FirebaseCrashlytics.log({ message: `--- log buffer ---\n${breadcrumbs}` })
    }

    await FirebaseCrashlytics.recordException({
      message,
      stacktrace: parseStack(stack),
      keysAndValues: toKeysAndValues(context),
    })
  } catch (reportingError) {
    console.warn('[Crashlytics] Failed to record exception', reportingError)
  }
}

/**
 * Pushes the current log buffer into Crashlytics so it is attached to whatever
 * report comes next.
 *
 * `recordError` already does this for JS-originated reports, but a purely native
 * crash (ANR, OOM, a plugin fault) never passes through it and would otherwise
 * carry no context at all. Flushing when the app leaves the foreground covers
 * that: the breadcrumbs are already in place if the process dies while backgrounded.
 */
export const flushBreadcrumbs = async (): Promise<void> => {
  if (!canReport()) {
    return
  }

  const breadcrumbs = formatRecentLogs(MAX_BREADCRUMB_LENGTH)
  if (!breadcrumbs) {
    return
  }

  await logBreadcrumb(`--- log buffer (flushed on background) ---\n${breadcrumbs}`)
}

/**
 * Adds a breadcrumb that is attached to the next crash report.
 */
export const logBreadcrumb = async (message: string): Promise<void> => {
  if (!canReport()) {
    return
  }

  try {
    await FirebaseCrashlytics.log({ message })
  } catch (error) {
    console.warn('[Crashlytics] Failed to log message', error)
  }
}

/**
 * Associates subsequent reports with a user, so a crash can be traced back to
 * the seller that hit it. Pass an empty string on logout.
 */
export const setCrashlyticsUserId = async (userId: string): Promise<void> => {
  if (!canReport()) {
    return
  }

  try {
    await FirebaseCrashlytics.setUserId({ userId })
  } catch (error) {
    console.warn('[Crashlytics] Failed to set user id', error)
  }
}

/**
 * Sets a custom key shown on every subsequent report (e.g. app version, channel).
 */
export const setCrashlyticsKey = async (key: string, value: string): Promise<void> => {
  if (!canReport()) {
    return
  }

  try {
    await FirebaseCrashlytics.setCustomKey({ key, value: truncate(value), type: 'string' })
  } catch (error) {
    console.warn('[Crashlytics] Failed to set custom key', error)
  }
}

/**
 * Forces a native crash. Only for verifying the Crashlytics pipeline end to end —
 * the report shows up in the console after the app is relaunched.
 */
export const forceTestCrash = async (): Promise<void> => {
  if (!canReport()) {
    console.warn('[Crashlytics] forceTestCrash is only available on a native build')
    return
  }

  await FirebaseCrashlytics.crash({ message: 'Test crash triggered from the JS layer' })
}

const handleWindowError = (event: ErrorEvent): void => {
  // Defensive: a failed <img>/<script> fires a non-bubbling error on the element,
  // so it cannot reach this bubble-phase listener today. The guard keeps that true
  // if anyone later switches this to capture phase.
  if (event.target && event.target !== window) {
    return
  }

  // Cross-origin script failures arrive opaque ("Script error." with no error
  // object, no file, no line). There is nothing to diagnose and reporting them
  // would attach a full log buffer to noise.
  if (!event.error && !event.filename) {
    return
  }

  void recordError(event.error ?? event.message, {
    source: 'window.onerror',
    fileName: event.filename ?? '',
    lineNumber: String(event.lineno ?? ''),
  })
}

const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
  void recordError(event.reason, { source: 'unhandledrejection' })
}

/**
 * Enables crash collection and starts forwarding uncaught JS errors, unhandled
 * promise rejections and `loggerService` errors to Crashlytics.
 * Safe to call more than once.
 */
export const initCrashlytics = async (): Promise<void> => {
  if (!handlersInstalled) {
    handlersInstalled = true

    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    loggerService.on('error', (error: unknown) => {
      void recordError(error, { source: 'loggerService' })
    })

    // Leaving the foreground is the last safe moment to attach context before a
    // native crash or an ANR kills the process.
    void App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        void flushBreadcrumbs()
      }
    }).catch(error => console.warn('[Crashlytics] Failed to observe app state', error))
  }

  if (nativeInitialized) {
    return
  }

  if (!canReport()) {
    console.log('[Crashlytics] Crash reporting unavailable on this platform/build')
    return
  }

  try {
    await FirebaseCrashlytics.setEnabled({ enabled: true })
    await setCrashlyticsKey('otaChannel', process.env.REACT_APP_OTA_CHANNEL || 'production')

    const { crashed } = await FirebaseCrashlytics.didCrashOnPreviousExecution()
    if (crashed) {
      console.warn('[Crashlytics] The app crashed during the previous execution')
    }

    // Only latched on success, so a transient failure stays retryable.
    nativeInitialized = true
  } catch (error) {
    console.warn('[Crashlytics] Failed to initialize', error)
  }
}
